/**
 * usePhotoQueue — exposes the photo upload queue for a vehicle and drains it.
 *
 * Mirrors useServiceQueue but for the 3-step photo upload protocol:
 *   1. POST /v1/photos/upload-intent  → { photoId, uploadUrl }
 *   2. PUT bytes to uploadUrl         (direct to signed storage URL)
 *   3. POST /v1/photos/:id/commit
 *
 * Binary bytes are read from IndexedDB via photoQueue.getBytes() so uploads
 * work after a page reload (offline-pick → reload → reconnect → drain).
 *
 * Signed uploadUrls expire (~15 min); if step 2 (PUT) terminally fails on a
 * long-queued item, the persisted photoId/uploadUrl are cleared so the next
 * attempt re-requests a fresh upload-intent instead of retrying a dead URL.
 *
 * Spec 011 — CRITICAL: `forServiceEntry: true` on the upload-intent for any
 * item with a `role` (before/after slot pick — see photoQueue.ts). graydon's
 * backend forks PUT /raw on this flag: false (default, general album pick)
 * streams straight to CAS and is NEVER stripped; true routes through
 * quarantine + the strip worker, the only path that ever sets `stripped_at`.
 * Without this flag, a slot photo goes the album route, never becomes
 * eligible for `beforePhoto`/`afterPhoto`, and the create-DTO's
 * committed+stripped gate rejects it forever (PHOTO_NOT_READY) — this is the
 * seam that makes the whole slice function, not a nice-to-have.
 *
 * Security (§00):
 *   - uid guard: drain NEVER uploads an item whose uid !== auth.currentUser.uid.
 *   - Single-flight mutex: module-level flag prevents concurrent drains.
 *
 * Drain triggers (mirrors useServiceQueue exactly):
 *   - online event
 *   - visibilitychange / window focus
 *   - Periodic tick (30s) when online
 */

import { auth } from '@/lib/firebase';
import {
  getBytes,
  listAll,
  listFor,
  remove,
  subscribe,
  update,
  type PhotoQueueItem,
  type StoredPhotoItem,
} from '@/lib/photoQueue';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOnlineStatus } from './useOnlineStatus';

// Module-level single-flight flag — shared across all hook instances.
let draining = false;

async function drainQueue(vehicleId?: string): Promise<void> {
  if (draining) return;
  if (!navigator.onLine) return;

  const currentUser = auth.currentUser;
  if (!currentUser) return;

  draining = true;
  try {
    const all = await listAll();
    const targets = all.filter(
      (q) =>
        q.status === 'pending' &&
        q.uid === currentUser.uid &&
        (vehicleId === undefined || q.vehicleId === vehicleId),
    );

    for (const item of targets) {
      if (auth.currentUser?.uid !== item.uid) continue;

      await update(item.clientId, { status: 'inflight' });

      try {
        const photoId = await uploadPhoto(item, currentUser);

        // Spec 011 — this photo was picked via a service-entry before/after
        // slot. Write the resolved photoId back onto the matching
        // service-queue entry BEFORE removing this item, since once it's
        // gone the clientId -> photoId mapping is unrecoverable (the slots
        // UI may already be unmounted if the entry form was closed offline).
        // sha256 isn't known yet (async strip-worker) — useServiceQueue.ts's
        // drain polls for that separately using the photoId recorded here.
        if (item.role && item.serviceEntryDraftId) {
          const { listAll: listServiceEntries, update: updateServiceEntry } =
            await import('@/lib/serviceQueue');
          const entries = await listServiceEntries();
          const target = entries.find((e) => e.clientMutationId === item.serviceEntryDraftId);
          if (target) {
            const nextPendingRefs = {
              ...target.pendingPhotoRefs,
              [item.role]: { photoId },
            };
            await updateServiceEntry(item.serviceEntryDraftId, {
              pendingPhotoRefs: nextPendingRefs,
            });
          }
        }

        await remove(item.clientId);
      } catch (err) {
        const isTerminal = isTerminalError(err);
        if (isTerminal) {
          await update(item.clientId, { status: 'failed' });
          console.warn('[photoQueue] drain: photo permanently failed', {
            clientId: item.clientId,
            vehicleId: item.vehicleId,
            status: 'failed',
          });
        } else {
          await update(item.clientId, { status: 'pending' });
          console.warn('[photoQueue] drain: photo reverted to pending', {
            clientId: item.clientId,
            vehicleId: item.vehicleId,
            status: 'pending',
          });
        }
      }
    }
  } finally {
    draining = false;
  }
}

interface FirebaseUser {
  uid: string;
  getIdToken: () => Promise<string>;
}

/** Returns the committed photoId — spec 011's role write-back needs it. */
async function uploadPhoto(item: StoredPhotoItem, user: FirebaseUser): Promise<string> {
  const { env } = await import('@/lib/env');

  // Step 1: upload-intent (if we don't already have photoId+uploadUrl from a
  // previous partial attempt).
  let photoId = item.photoId;
  let uploadUrl = item.uploadUrl;

  if (!photoId || !uploadUrl) {
    const token = await user.getIdToken();
    const intentRes = await fetch(`${env.VITE_GG_API_BASE_URL}/v1/photos/upload-intent`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        vehicleId: item.vehicleId,
        contentType: item.contentType,
        sizeBytes: item.sizeBytes,
        capturedAt: item.capturedAt,
        ...(item.caption ? { caption: item.caption } : {}),
        // Spec 011 — a `role`-tagged item is a before/after slot pick; MUST
        // route through quarantine + strip (see file header). Omitted
        // (server default false) for ordinary album picks.
        ...(item.role ? { forServiceEntry: true } : {}),
      }),
    });

    if (!intentRes.ok) {
      throw Object.assign(new Error(`upload-intent ${intentRes.status}`), {
        status: intentRes.status,
      });
    }

    const intent = (await intentRes.json()) as { photoId: string; uploadUrl: string };
    photoId = intent.photoId;
    uploadUrl = intent.uploadUrl;

    // Persist intent result so a crash between step 1 and 2 can resume at step 2.
    await update(item.clientId, { photoId, uploadUrl });
  }

  // Step 2: PUT the bytes to the signed uploadUrl.
  const bytes = await getBytes(item.clientId);
  if (!bytes) {
    // Bytes missing (e.g. different browser profile) — terminal failure.
    throw Object.assign(new Error('bytes missing from IndexedDB'), { status: 422 });
  }

  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': item.contentType },
    body: bytes,
  });

  if (!putRes.ok) {
    const err = Object.assign(new Error(`PUT bytes ${putRes.status}`), { status: putRes.status });
    if (isTerminalError(err)) {
      // Signed upload URLs expire (~15 min). A terminal PUT failure this far into an
      // offline-queued item almost always means the persisted URL went stale — clear
      // it so the next attempt re-requests a fresh upload-intent instead of retrying
      // the same dead URL forever.
      await update(item.clientId, { photoId: undefined, uploadUrl: undefined });
    }
    throw err;
  }

  // Step 3: commit.
  const token = await user.getIdToken();
  const commitRes = await fetch(
    `${env.VITE_GG_API_BASE_URL}/v1/photos/${encodeURIComponent(photoId)}/commit`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!commitRes.ok) {
    throw Object.assign(new Error(`commit ${commitRes.status}`), { status: commitRes.status });
  }

  return photoId;
}

function isTerminalError(err: unknown): boolean {
  if (err instanceof Error && 'status' in err) {
    const status = (err as { status: number }).status;
    // 4xx (non-auth, non-retry-able) are terminal.
    return status >= 400 && status < 500 && status !== 401 && status !== 429;
  }
  return false;
}

/** Exposed for testing — reset the single-flight flag between tests. */
export function _resetDrainFlag(): void {
  draining = false;
}

export interface PhotoQueueResult {
  /** Pending and failed queue items for this vehicle, enriched with previewUrl. */
  items: PhotoQueueItem[];
  /** Manually trigger a drain. */
  drain: () => Promise<void>;
  /** Retry a failed item (reset to pending, re-drain). */
  retry: (clientId: string) => Promise<void>;
  /** Discard a failed item. */
  discard: (clientId: string) => Promise<void>;
}

/**
 * usePhotoQueue(vehicleId) — subscribe to the photo upload queue for a vehicle.
 *
 * previewUrls are managed as runtime state in this hook: on first load,
 * if an item has bytes in IndexedDB, we create an object URL for it and
 * store it in local state. These are revoked on unmount.
 */
export function usePhotoQueue(vehicleId: string): PhotoQueueResult {
  const [items, setItems] = useState<PhotoQueueItem[]>([]);
  const isOnline = useOnlineStatus();
  const vehicleIdRef = useRef(vehicleId);
  const previewUrlsRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    vehicleIdRef.current = vehicleId;
  });

  // Revoke all managed object URLs on unmount.
  useEffect(() => {
    const urls = previewUrlsRef.current;
    return () => {
      for (const url of urls.values()) {
        URL.revokeObjectURL(url);
      }
      urls.clear();
    };
  }, []);

  const refresh = useCallback(async () => {
    const stored = await listFor(vehicleIdRef.current);
    const visible = stored.filter((q) => q.status !== 'inflight');

    // Attach previewUrls: prefer the map (runtime) for already-created URLs.
    const enriched: PhotoQueueItem[] = await Promise.all(
      visible.map(async (q) => {
        let previewUrl = previewUrlsRef.current.get(q.clientId);
        if (!previewUrl) {
          const bytes = await getBytes(q.clientId);
          if (bytes) {
            const blob = new Blob([bytes], { type: q.contentType });
            previewUrl = URL.createObjectURL(blob);
            previewUrlsRef.current.set(q.clientId, previewUrl);
          }
        }
        // exactOptionalPropertyTypes: only spread previewUrl when it has a value.
        if (previewUrl) {
          return { ...q, previewUrl };
        }
        return { ...q };
      }),
    );

    setItems(enriched);
  }, []);

  const triggerDrain = useCallback(async () => {
    await drainQueue(vehicleIdRef.current);
    await refresh();
  }, [refresh]);

  const retry = useCallback(
    async (clientId: string) => {
      // Belt-and-braces: also clear any stale upload-intent here, in case an item
      // was left in a stale state some other way than the PUT-failure path above.
      await update(clientId, { status: 'pending', photoId: undefined, uploadUrl: undefined });
      await triggerDrain();
    },
    [triggerDrain],
  );

  const discard = useCallback(
    async (clientId: string) => {
      // Revoke preview URL if present.
      const url = previewUrlsRef.current.get(clientId);
      if (url) {
        URL.revokeObjectURL(url);
        previewUrlsRef.current.delete(clientId);
      }
      await remove(clientId);
      await refresh();
    },
    [refresh],
  );

  // Initial load and when vehicleId changes.
  useEffect(() => {
    void refresh();
  }, [vehicleId, refresh]);

  // Re-read immediately on any queue mutation.
  useEffect(() => subscribe(() => void refresh()), [refresh]);

  // Reconnect drain.
  useEffect(() => {
    if (!isOnline) return;
    void triggerDrain();
  }, [isOnline, triggerDrain]);

  // Robust triggers: online event, visibilitychange, window focus, 30s tick.
  useEffect(() => {
    const onOnline = () => void triggerDrain();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void triggerDrain();
    };
    const onFocus = () => void triggerDrain();

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    const tick = setInterval(() => {
      if (navigator.onLine) void triggerDrain();
    }, 30_000);

    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      clearInterval(tick);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { items, drain: triggerDrain, retry, discard };
}
